import { useEffect, useState } from "react";
import type {
  HighlightRequest,
  HighlightResponse,
} from "@/workers/highlightWorker";

// --- djb2 hash ---
function djb2(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
  }
  return hash.toString(36);
}

function makeCacheKey(code: string, lang: string): string {
  return djb2(`${lang}:${code}`);
}

// --- LRU cache ---
const MAX_CACHE_SIZE = 200;
const cache = new Map<string, string>();

function cacheGet(key: string): string | undefined {
  const value = cache.get(key);
  if (value !== undefined) {
    // Move to end (most recently used)
    cache.delete(key);
    cache.set(key, value);
  }
  return value;
}

function cacheSet(key: string, value: string): void {
  if (cache.has(key)) {
    cache.delete(key);
  } else if (cache.size >= MAX_CACHE_SIZE) {
    // Evict least recently used (first entry)
    const first = cache.keys().next().value!;
    cache.delete(first);
  }
  cache.set(key, value);
}

// --- Worker singleton ---
let worker: Worker | null = null;
let nextId = 0;
const pending = new Map<number, (html: string) => void>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL("../workers/highlightWorker.ts", import.meta.url),
      { type: "module" }
    );
    worker.onmessage = (e: MessageEvent<HighlightResponse>) => {
      const { id, html } = e.data;
      const resolve = pending.get(id);
      if (resolve) {
        resolve(html);
        pending.delete(id);
      }
    };
  }
  return worker;
}

/** Initialize the highlight worker early (call from requestIdleCallback). */
export function initHighlightWorker(): void {
  getWorker();
}

/**
 * Highlight code via the Shiki web worker with dual themes (light + dark).
 * Both themes are baked into CSS variables in a single HTML output —
 * no re-highlighting needed on theme toggle.
 * Returns cached HTML synchronously on Virtuoso remount, or null while the worker processes.
 */
export function useShikiHighlighter(
  code: string,
  language: string | undefined,
): string | null {
  const lang = language ?? "text";
  const key = makeCacheKey(code, lang);
  const cached = cacheGet(key);

  const [html, setHtml] = useState<string | null>(cached ?? null);

  useEffect(() => {
    // Already have it cached — no need to post to worker
    if (cached) {
      setHtml(cached);
      return;
    }

    const id = nextId++;
    const w = getWorker();

    pending.set(id, (result: string) => {
      cacheSet(key, result);
      setHtml(result);
    });

    w.postMessage({
      id,
      code,
      language: lang,
    } satisfies HighlightRequest);

    return () => {
      pending.delete(id);
    };
  }, [code, lang, key, cached]);

  return html;
}
